import {
  MOCK_SCHOOL,
  MOCK_SCHOOL_YEAR,
  MOCK_CLASSES,
  MOCK_TARIFFS,
  MOCK_DISCOUNT_RULES,
  MOCK_PROFILES,
  MOCK_STUDENTS,
  MOCK_TEACHER_LINKS,
  MOCK_PARENT_LINKS,
  MOCK_INVOICES,
  MOCK_PAYMENTS,
  MOCK_RECEIPTS,
  MOCK_PAYMENT_PROOFS,
  MOCK_PROOF_INVOICES,
  MOCK_GROUPS,
} from './mockData';

// Mutable in-memory stores for runtime session changes
const db = {
  schools: [...MOCK_SCHOOL ? [MOCK_SCHOOL] : []],
  school_years: [...MOCK_SCHOOL_YEAR ? [MOCK_SCHOOL_YEAR] : []],
  classes: [...MOCK_CLASSES],
  groups: [...MOCK_GROUPS || []],
  tariffs: [...MOCK_TARIFFS],
  discount_rules: [...MOCK_DISCOUNT_RULES],
  profiles: [...MOCK_PROFILES],
  students: [...MOCK_STUDENTS],
  teacher_class_links: [...MOCK_TEACHER_LINKS],
  parent_student_links: [...MOCK_PARENT_LINKS],
  invoices: [...MOCK_INVOICES],
  payments: [...MOCK_PAYMENTS],
  receipts: [...MOCK_RECEIPTS],
  payment_proofs: [...MOCK_PAYMENT_PROOFS],
  proof_invoices: [...MOCK_PROOF_INVOICES],
  audit_logs: [],
};

function filterRows(rows, searchParams) {
  let result = [...rows];

  for (const [key, val] of searchParams.entries()) {
    if (key === 'select' || key === 'order' || key === 'limit' || key === 'offset' || key === 'count') {
      continue;
    }

    if (val.startsWith('eq.')) {
      const target = val.slice(3);
      result = result.filter((r) => {
        const fieldVal = r[key];
        if (target === 'true') return fieldVal === true;
        if (target === 'false') return fieldVal === false;
        if (target === 'null') return fieldVal == null;
        return String(fieldVal) === target;
      });
    } else if (val.startsWith('in.(')) {
      const inner = val.slice(4, -1);
      const items = inner.split(',').map((s) => s.trim().replace(/^["']|["']$/g, ''));
      result = result.filter((r) => items.includes(String(r[key])));
    }
  }

  return result;
}

export function createSupabaseFetch({ isConfigured }) {
  return async function customFetch(input, init = {}) {
    const rawUrl = typeof input === 'string' ? input : (input?.url || '');
    let url;
    try {
      url = new URL(rawUrl, 'https://placeholder-project.supabase.co');
    } catch {
      url = new URL('https://placeholder-project.supabase.co');
    }

    // If configured with real credentials, attempt live fetch first with defensive timeout & fallback
    if (isConfigured) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3500);
        const res = await fetch(input, { ...init, signal: controller.signal });
        clearTimeout(timer);
        if (res.status < 500) {
          return res;
        }
      } catch (err) {
        console.warn('[MTSRpay] Supabase network error caught, falling back to local store:', err?.message || err);
        // Jatuh ke pelaksanaan memori di bawah untuk memastikan aplikasi tidak tergantung
      }
    }

    // Mock implementation when Supabase is not configured
    const pathname = url.pathname;
    const method = (init.method || 'GET').toUpperCase();
    const headers = init.headers || {};
    const acceptHeader = typeof headers.get === 'function' ? headers.get('Accept') : (headers.Accept || headers.accept || '');

    // 1. Auth endpoints
    if (pathname.includes('/auth/v1/signup')) {
      let bodyObj = {};
      try {
        bodyObj = JSON.parse(init.body || '{}');
      } catch {
        bodyObj = {};
      }
      const email = (bodyObj.email || '').toLowerCase().trim();
      const newUserId = `usr-user-${Date.now()}`;
      const user = {
        id: newUserId,
        aud: 'authenticated',
        role: 'authenticated',
        email,
        user_metadata: bodyObj.data || { nama: 'Pengguna Baru' },
        app_metadata: { provider: 'email' },
        created_at: new Date().toISOString(),
      };
      return new Response(
        JSON.stringify({
          access_token: `mock-token-${newUserId}`,
          token_type: 'bearer',
          expires_in: 7200,
          refresh_token: `mock-refresh-${newUserId}`,
          user,
          session: {
            access_token: `mock-token-${newUserId}`,
            token_type: 'bearer',
            expires_in: 7200,
            refresh_token: `mock-refresh-${newUserId}`,
            user,
          },
        }),
        { status: 200, headers: { 'Content-Type': 'application/json' } }
      );
    }

    if (pathname.includes('/auth/v1/token')) {
      let bodyObj = {};
      try {
        bodyObj = JSON.parse(init.body || '{}');
      } catch {
        bodyObj = {};
      }

      const email = (bodyObj.email || '').toLowerCase().trim();
      const matchedProfile = db.profiles.find((p) => p.email.toLowerCase() === email) || db.profiles[0];

      return new Response(
        JSON.stringify({
          access_token: `mock-token-${matchedProfile.id}`,
          token_type: 'bearer',
          expires_in: 7200,
          refresh_token: `mock-refresh-${matchedProfile.id}`,
          user: {
            id: matchedProfile.id,
            aud: 'authenticated',
            role: 'authenticated',
            email: matchedProfile.email,
            user_metadata: { nama: matchedProfile.nama },
            app_metadata: { provider: 'email' },
            created_at: '2026-01-01T00:00:00Z',
          },
        }),
        { status: 200, headers: { 'Content-Type': 'application/json' } }
      );
    }

    if (pathname.includes('/auth/v1/user')) {
      const user = {
        id: 'usr-bendahara',
        aud: 'authenticated',
        role: 'authenticated',
        email: 'bendaharamtsr@gmail.com',
        user_metadata: { nama: 'Ustaz Bendahara MTSR' },
        created_at: '2026-01-01T00:00:00Z',
      };
      return new Response(JSON.stringify(user), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    if (pathname.includes('/auth/v1/logout') || pathname.includes('/auth/v1/recover')) {
      return new Response(JSON.stringify({}), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    // 2. RPC endpoints (/rest/v1/rpc/<name>)
    if (pathname.includes('/rest/v1/rpc/')) {
      const rpcName = pathname.split('/rest/v1/rpc/')[1];
      let body = {};
      try {
        body = JSON.parse(init.body || '{}');
      } catch {
        body = {};
      }

      if (rpcName === 'list_classes_public') {
        const schoolName = db.schools[0]?.nama || 'Sekolah Menengah Agama MTSR';
        const schoolId = db.schools[0]?.id || 'sch-1';
        const list = db.classes.map((c) => ({
          class_id: c.id,
          kelas: c.nama,
          sekolah: schoolName,
          school_id: c.school_id || schoolId,
        }));
        return new Response(JSON.stringify(list), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      if (rpcName === 'register_guru_links') {
        return new Response(JSON.stringify({ ok: true }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      if (rpcName === 'preview_receipt_no' || rpcName === 'next_receipt_no') {
        return new Response(JSON.stringify('R-2026-0003'), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      if (rpcName === 'record_payment') {
        return new Response(
          JSON.stringify({ payment_id: 'pay-new', receipt_no: 'R-2026-0004' }),
          { status: 200, headers: { 'Content-Type': 'application/json' } }
        );
      }
      if (rpcName === 'approve_proof') {
        return new Response(
          JSON.stringify({ receipt_no: 'R-2026-0005' }),
          { status: 200, headers: { 'Content-Type': 'application/json' } }
        );
      }
      if (rpcName === 'reject_proof') {
        return new Response(JSON.stringify({ ok: true }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      if (rpcName === 'generate_billing') {
        return new Response(JSON.stringify({ count: 5 }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      if (rpcName === 'search_students_masked') {
        const q = (body.p_query || '').toLowerCase();
        const matches = db.students
          .filter((s) => s.nama.toLowerCase().includes(q) || s.no_pelajar.toLowerCase().includes(q))
          .map((s) => ({
            id: s.id,
            nama: s.nama,
            no_pelajar: s.no_pelajar,
            kelas: db.classes.find((c) => c.id === s.class_id)?.nama || 'Tahun 1',
          }));
        return new Response(JSON.stringify(matches), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      return new Response(JSON.stringify({ ok: true }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    // 3. PostgREST table queries (/rest/v1/<tableName>)
    const table = pathname.replace(/^\/rest\/v1\//, '').split('?')[0];
    const tableData = db[table] || [];

    if (method === 'GET') {
      const filtered = filterRows(tableData, url.searchParams);
      const isSingle = acceptHeader.includes('vnd.pgrst.object+json');

      if (isSingle) {
        const single = filtered[0] || null;
        return new Response(JSON.stringify(single), {
          status: 200,
          headers: {
            'Content-Type': 'application/json',
            'Content-Range': `0-0/${filtered.length}`,
          },
        });
      }

      return new Response(JSON.stringify(filtered), {
        status: 200,
        headers: {
          'Content-Type': 'application/json',
          'Content-Range': `0-${Math.max(0, filtered.length - 1)}/${filtered.length}`,
        },
      });
    }

    if (method === 'POST') {
      let body = {};
      try {
        body = JSON.parse(init.body || '{}');
      } catch {
        body = {};
      }
      const newItems = Array.isArray(body) ? body : [body];
      newItems.forEach((it, idx) => {
        if (!it.id) it.id = `${table}-${Date.now()}-${idx}`;
        if (!db[table]) db[table] = [];
        db[table].push(it);
      });

      return new Response(JSON.stringify(Array.isArray(body) ? newItems : newItems[0]), {
        status: 201,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    if (method === 'PATCH' || method === 'DELETE') {
      return new Response(JSON.stringify({ ok: true }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    return new Response(JSON.stringify([]), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  };
}
